import {
  COOLDOWN_POSE,
  PINNED_CALIBRATION_POSES,
  TURO_POSES,
  type PoseFrame,
  type TuroPose,
} from './turo-world-poses'
import { TURO_PRAISE_KEYS } from './turo-world-voice'

/**
 * Turo World Level 1's session, as data — TW-5's other half.
 *
 * 🔴 STRUCTURE ONLY. NO DURATIONS. Draft §4.3 asks for *"beat count, pose count,
 * and per-beat duration"* to come from the level document; the first two are
 * here and the third is not, because `curriculum/types.ts` already refuses a
 * duration on a story beat — *"🔴 NO DURATION, NO TOLERANCE, NO DIFFICULTY, and
 * that is ADR-031 extended to narrative"* — and `check-game-contract.mjs` fails
 * on a game declaring a timing constant. Pacing lives in
 * `engine/warmup/beatMachine.ts`, which that check exempts.
 *
 * ⚠️ WHAT THAT COSTS, SAID PLAINLY: Level 2 can change which poses and how many,
 * but not how long a beat runs. §4's *"Level 2 should require zero runtime
 * changes to ship"* holds for content and not for pacing. If a level ever needs
 * its own tempo, that is an ADR, not a field.
 *
 * ─── 🔴 BEATS 3 AND 4 ARE PINNED, AND THAT IS A CALIBRATION DECISION ─────────
 *
 * §2.1: *"`t_pose` and `arms_up` are scheduled first in every session, always…
 * Pose order is a calibration decision, not an aesthetic one. Do not let a later
 * content pass shuffle them out of position."* They are the two poses that put
 * the wrists furthest from the torso with nothing occluded, which is the frame a
 * body-scale measurement wants. `turo-world-session.test.ts` asserts their
 * position rather than trusting this comment.
 */

/**
 * The shape `engine/warmup` runs.
 *
 * 🔴 L2's `DO` / `WATCH` / `FREE` ALL MAP TO `pose`, AND THAT IS DELIBERATE.
 * `TURO_WORLD_L2_L3_PLAN.md` §3 names six beat types; three of them differ only
 * in whether the child is graded and whether a ghost shows — and the POSE
 * already declares both (`graded`, and an empty `ghost` for a WATCH scene).
 * Adding three beat kinds that must stay byte-identical to `pose` is plan C-1's
 * warning about a duplicate `practice` kind, one level down: two shapes that
 * must agree will drift, and the drift will be silent.
 *
 * Only `talk`, `consent` and `choose` are genuinely new — none of them is a pose
 * at all.
 */
export interface SessionBeat {
  kind:
    | 'frameCheck'
    | 'greeting'
    | 'intro'
    | 'pose'
    | 'cooldown'
    | 'sessionEnd'
    /** Narration with nothing to do — L2's TALK. */
    | 'talk'
    /** The camera question, and the only genuine fork in the level. */
    | 'consent'
    /** `stepZone` tiles the child picks between — L2 S1's choose beat. */
    | 'choose'
    /**
     * One item from L3's scenario bank — card, narration, 2–3 tiles.
     *
     * ⚠️ DISTINCT FROM `choose`, THOUGH BOTH ARE TILES. L2's choose beat has no
     * right answer by design — §3.3: "no wrong answer, no scoring". A scenario
     * does have one, and is graded. Collapsing them would let a scoring path
     * reach the beat whose entire lesson is that the child's choice is honoured.
     */
    | 'scenario'
  poseId?: string
  /**
   * Which level this beat belongs to.
   *
   * 🔴 THE LEVEL LIVES ON THE BEAT RATHER THAN IN A SECOND BEAT KIND — plan C-1.
   * L1 is ALREADY no-fail: `beatMachine.ts` picks the praise line before the
   * outcome exists and is byte-identical on a met hold and a timeout. So L2's
   * "practice" is not a new machine, it is a flag the validator can assert.
   */
  level?: 1 | 2 | 3
  /** Voice line ids this beat speaks, in order. */
  lines?: readonly string[]
  /** Tile ids for a `choose` beat. */
  tiles?: readonly string[]
  /** Scenario id for a `scenario` beat — see `turo-world-l3-scenarios.ts`. */
  scenarioId?: string
  /**
   * A beat a school may switch off.
   *
   * ⚠️ MODELLED AS A FLAG, NOT AS AN OMITTED BEAT (plan §3.1 decision 9). The
   * `body.clothes` line must exist in the audio manifest and in the reviewer
   * package whether or not a given school plays it — and a school turning it on
   * must not trigger a content download.
   */
  optional?: boolean
}

export interface TuroSession {
  beats: readonly SessionBeat[]
  praisePool: readonly string[]
}

/**
 * Build a session for L2 or L3 from its authored beat list.
 *
 * 🔴 L1 IS SAMPLED, L2 AND L3 ARE AUTHORED, AND THAT IS THE REAL DIFFERENCE.
 * `buildTuroSession` PICKS poses — a first run takes five, a repeat takes three,
 * from a fixed rotation. L2 and L3 are taught sequences: §3.1 fixes their order
 * 1→2→3→4 and validator rule 13 forbids shuffling. So there is nothing to
 * sample; the beat list IS the session.
 *
 * ⚠️ THE PRAISE POOL IS SHARED WITH L1 ON PURPOSE. §7: *"the celebration lines
 * must be indistinguishable in energy from L1's — a three-year-old reads tone
 * perfectly and hears any softening as failure."* A separate pool is how that
 * softening creeps in.
 */
export function buildAuthoredSession(
  beats: readonly SessionBeat[],
  praisePool: readonly string[],
): TuroSession {
  return { beats, praisePool }
}

/**
 * 🔴 HOW MANY POSES A SITTING ASKS FOR — §5's beat sheet.
 *
 * Six either way, like every level of every game; a repeat run still skips the
 * intro. It was five and three — §5's draft shortened the repeat for attention.
 */
export const FIRST_RUN_POSES = 6
export const REPEAT_RUN_POSES = 6

export interface SessionOptions {
  /** First sitting ever: the intro beat plays and the session is longer. */
  firstRun: boolean
  /** What Beat 0 found. `upper` serves only upper-body poses — §6.2. */
  frameMode: PoseFrame
  /**
   * Which of the eligible poses to take, given how many are wanted.
   *
   * ⚠️ INJECTED SO THE SESSION IS TESTABLE WITHOUT A CLOCK OR A RANDOM SOURCE,
   * the same argument `sampleRound` makes in the rest of the curriculum. The
   * default is a FIXED ROTATION rather than a shuffle — open item 4 answers
   * itself in the draft: *"random is more interesting; fixed is more ritual, and
   * three-year-olds want ritual."*
   */
  pick?: (eligible: readonly TuroPose[], want: number) => readonly TuroPose[]
}

/** Fixed rotation: take them in bank order. Ritual over novelty. */
const inOrder = (eligible: readonly TuroPose[], want: number): readonly TuroPose[] =>
  eligible.slice(0, want)

/**
 * The poses a session may draw on beyond the pinned two, **in preference
 * order**.
 *
 * ⚠️ GRADED ONLY. A free pose always succeeds, so filling beats 5–7 with them
 * would produce a session that asks nothing — pleasant, and not the game. The
 * cool-down is where a free pose belongs, and it has its own beat.
 *
 * 🔴 FULL-FRAME POSES COME FIRST WHEN THERE IS ROOM FOR THEM, AND THIS ORDERING
 * IS LOAD-BEARING RATHER THAN COSMETIC.
 *
 * The bank happens to list the three upper poses before the three full ones, so
 * a plain "take the first three" gave a child standing in a clear room the
 * IDENTICAL session to one sitting at a desk — `star_jump`, `knee_raise` and
 * `squat` never played at all. Every graded movement in that session is an arm.
 *
 * ADR-031 Amendment 1 is the reason that is a bug and not a preference: *"if
 * every category could be answered by pointing, Kalqy becomes a tablet app that
 * happens to own a webcam."* A warm-up whose whole point is moving the body,
 * played by a child with the floor space to jump, must ask them to jump. Upper
 * poses still fill whatever is left over.
 */
/*
 * ⚠️ NO REPETITION POSES UNTIL SOMETHING COUNTS THEM. `star_jump` (×4) and
 * `march` (×6) are graded as a single 1 s hold — `TwRound` has no rep counter —
 * so a child really jumping or marching rarely held still long enough to pass,
 * while one standing with arms out or a knee up did. `knee_raise` and `squat`
 * keep the full-body beats; the two return when the vision kit counts reps.
 */
export function eligiblePoses(frameMode: PoseFrame): readonly TuroPose[] {
  const eligible = TURO_POSES.filter(
    (p) =>
      p.graded &&
      p.type !== 'repetition' &&
      !PINNED_CALIBRATION_POSES.includes(p.id) &&
      (frameMode === 'full' || p.frame === 'upper'),
  )
  if (frameMode === 'upper') return eligible
  return [...eligible.filter((p) => p.frame === 'full'), ...eligible.filter((p) => p.frame !== 'full')]
}

/**
 * Build Level 1's session.
 *
 * ⚠️ IT CAN COME UP SHORT, AND IT SAYS SO BY BEING SHORT RATHER THAN BY
 * REPEATING A POSE. In `upper` framing there are five eligible poses, so a run
 * gets 2 pinned + 4 = six; ask for more than the pool holds and the session is
 * simply shorter. Padding it by repeating a pose
 * would show a child the same movement twice in two minutes and look deliberate.
 */
export function buildTuroSession({
  firstRun,
  frameMode,
  pick = inOrder,
}: SessionOptions): TuroSession {
  const want = (firstRun ? FIRST_RUN_POSES : REPEAT_RUN_POSES) - PINNED_CALIBRATION_POSES.length
  const chosen = pick(eligiblePoses(frameMode), Math.max(0, want))

  const beats: SessionBeat[] = [{ kind: 'frameCheck' }, { kind: 'greeting' }]
  if (firstRun) beats.push({ kind: 'intro' })

  for (const id of PINNED_CALIBRATION_POSES) beats.push({ kind: 'pose', poseId: id })
  for (const p of chosen) beats.push({ kind: 'pose', poseId: p.id })

  beats.push({ kind: 'cooldown', poseId: COOLDOWN_POSE })
  beats.push({ kind: 'sessionEnd' })

  return { beats, praisePool: TURO_PRAISE_KEYS }
}
